import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import { EmptyPageFolder } from './WorkspaceEmptyPages';

import { cn } from '@distribute/frontend/utils';

import {
  DeleteFolderModal,
  foldersModel,
  RenameFolderModal,
} from '../../../features/folders';

import {
  DeletePageModal,
  pagesModel,
  MoveToFolderModal,
} from '../../../features/pages';

import { useSelector } from 'react-redux';
import { ShareFolderModal } from '../../../features/folders';

import { useTeamPermissions } from '../../../features/teams';

import { WorkspaceShareModal } from './WorkspaceShareModal';

import { useFilteredPages, usePageActions, useView } from '../hooks';

import { WorkspaceFilters } from './WorkspaceFilters';
import { WorkspaceFolderAction } from './WorkspaceFolderAction';

import { PagesList, PagesGrid } from './WorkspacePagesView';

export const WorkspaceFolderPage: React.FC = () => {
  const {
    currentPage,
    isDeletePageModalOpen,
    isMovePageToFolderModalOpen,
    isSharePageModalOpen,
    onDeletePageOpen,
    onDeletePageClose,
    onMoveToFolderOpen,
    onMoveToFolderClose,
    handleSharePage,
    handleSharePageClose,
  } = usePageActions();

  const { view } = useView();

  const pages = useSelector(pagesModel.selectors.selectPages);

  const currentFolder = useSelector(
    foldersModel.selectors.selectCurrentFolderWithError
  );

  const folderPages = useMemo(() => {
    return pages.filter((page) => page.folderId === currentFolder.id);
  }, [currentFolder, pages]);

  const filteredPages = useFilteredPages({
    pages: folderPages,
  });

  const [isDeleteFolderModalOpen, setIsDeleteFolderModalOpen] = useState(false);
  const [isRenameFolderModalOpen, setIsRenameFolderModalOpen] = useState(false);
  const [isShareFolderModalOpen, setIsShareFolderModalOpen] = useState(false);

  const { isGuest } = useTeamPermissions();

  return (
    <div className="sm:px-1 sm:pt-1 min-w-0 flex flex-col flex-1 gap-y-6">
      <Helmet titleTemplate={`${currentFolder.title} - Distribute`}></Helmet>
      <DeleteFolderModal
        isOpen={isDeleteFolderModalOpen}
        onClose={() => setIsDeleteFolderModalOpen(false)}
        folder={currentFolder}
      />
      <RenameFolderModal
        key={currentFolder.id}
        isOpen={isRenameFolderModalOpen}
        onClose={() => setIsRenameFolderModalOpen(false)}
        folder={currentFolder}
      />
      <ShareFolderModal
        isOpen={isShareFolderModalOpen}
        onClose={() => setIsShareFolderModalOpen(false)}
        folder={currentFolder}
      />
      {currentPage && (
        <>
          <DeletePageModal
            isOpen={isDeletePageModalOpen}
            onClose={onDeletePageClose}
            page={currentPage}
          />
          <MoveToFolderModal
            isOpen={isMovePageToFolderModalOpen}
            onClose={onMoveToFolderClose}
            page={currentPage}
          />
        </>
      )}
      <WorkspaceShareModal
        isOpen={isSharePageModalOpen}
        onClose={handleSharePageClose}
      />
      {isGuest ? (
        <>
          <p className="font-medium text-gray-900 truncate text-display-sm font-display">
            {currentFolder.title}
          </p>
          {folderPages.length > 0 && (
            <hr className="w-full bg-gray-200 sm:hidden" />
          )}
        </>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex flex-nowrap gap-x-3 items-center md:items-stretch md:gap-y-4 md:flex-col min-h-10">
            <p className="font-medium text-gray-900 truncate text-display-sm font-display">
              {currentFolder.title}
            </p>
            <WorkspaceFolderAction
              onRenameFolder={() => setIsRenameFolderModalOpen(true)}
              onDeleteFolder={() => setIsDeleteFolderModalOpen(true)}
              onShareFolder={() => setIsShareFolderModalOpen(true)}
            />
            <WorkspaceFilters />
          </div>
          {folderPages.length > 0 && (
            <hr className="w-full bg-gray-200 md:hidden" />
          )}
        </div>
      )}
      <div
        className={cn('flex-grow-1 flex flex-col sm:pb-0', {
          'pb-6': filteredPages.length > 0,
        })}
      >
        {filteredPages.length ? (
          view === 'grid' ? (
            <PagesGrid
              pages={filteredPages}
              onDeletePage={onDeletePageOpen}
              onMoveToFolder={onMoveToFolderOpen}
              onSharePage={handleSharePage}
            />
          ) : (
            <PagesList
              pages={filteredPages}
              onDeletePage={onDeletePageOpen}
              onMoveToFolder={onMoveToFolderOpen}
              onSharePage={handleSharePage}
            />
          )
        ) : (
          <EmptyPageFolder />
        )}
      </div>
    </div>
  );
};
